import { Button, Flex, Segmented, Tooltip } from 'antd';
import { BiShapePolygon, BiShapeTriangle } from 'react-icons/bi';
import { BsFillEraserFill } from 'react-icons/bs';
import { FiEdit3, FiMousePointer } from 'react-icons/fi';
import { TbRotate360 } from 'react-icons/tb';
import { VscChromeMaximize, VscRecord } from 'react-icons/vsc';

import { useProject } from '@contexts/Project.context';

import { useAnnotationDrawShortcuts } from './useAnnotationDrawShortcuts';

const ERASE = 'erase';
const DRAW = 'draw';
const SELECT = 'select';

const TOOL_ICONS = {
	Box: VscChromeMaximize,
	Circle: VscRecord,
	Freehand: BiShapePolygon,
	Polygon: BiShapeTriangle,
};

// An option's icon and text; in the compact toolbar the icon alone, its text kept
// as the accessible name. Shortcuts are named in the picker's tooltip.
const optionLabel = (Icon, text, compact) =>
	compact ? (
		<span aria-label={text} title={text}>
			<Icon aria-hidden />
		</span>
	) : (
		<Flex align="center" gap="small">
			<Icon aria-hidden />
			{text}
		</Flex>
	);

/**
 * The draw tools of the object annotation toolbar. Two shapes of it:
 *
 * - Mode picker (`onModeChange` given): a `Segmented` Draw (D) / Select (V), the
 *   shape picker (the draw shapes of the current model type) only while drawing,
 *   and a Rotate toggle. Boxes are edited by selecting them.
 * - Legacy picker: one `Segmented` of the shapes plus Erase.
 *
 * The shapes come from the project context (`availableAnnotationDrawTools` by
 * `modelType`), as the legacy `AnnotationDrawTools` reads them.
 *
 * @param {boolean} props.drawing - false while the eraser is active (legacy picker)
 * @param {() => void} [props.startDraw] - legacy picker
 * @param {() => void} [props.startErase] - legacy picker
 * @param {boolean} [props.showErase] - offer Erase (default true)
 * @param {'draw'|'select'} [props.mode] - the mode picker's current mode
 * @param {(mode: 'draw'|'select') => void} [props.onModeChange] - turns the mode picker on
 * @param {boolean} [props.disabled]
 * @param {boolean} [props.compact] - icons only (a narrow toolbar)
 * @param {boolean} [props.keysDisabled] - a dialog covers the toolbar: the D/V/E/R keys
 *   listen on `window`, so they stand down while the tools themselves stay as they are
 */
const DrawToolPicker = ({
	drawing,
	startDraw,
	startErase,
	showErase = true,
	mode,
	onModeChange,
	disabled = false,
	compact = false,
	keysDisabled = false,
}) => {
	const {
		modelType,
		annotationDrawTool,
		availableAnnotationDrawTools,
		isRotateEnabled,
		dispatch,
	} = useProject();

	const tools = availableAnnotationDrawTools[modelType] ?? [];
	const hasModes = Boolean(onModeChange);
	const isDrawing = hasModes ? mode === DRAW : drawing;

	const toggleRotation = () =>
		dispatch({ type: 'setIsRotateEnabled', payload: !isRotateEnabled });

	const goDraw = hasModes ? () => onModeChange(DRAW) : startDraw;
	useAnnotationDrawShortcuts({
		enabled: !disabled && !keysDisabled,
		startDraw: goDraw,
		startSelect: hasModes ? () => onModeChange(SELECT) : undefined,
		startErase: showErase ? startErase : undefined,
		toggleRotation,
	});

	const shapeOptions = [
		...tools.map(tool => ({
			value: tool,
			label: optionLabel(TOOL_ICONS[tool], tool, compact),
		})),
		...(showErase
			? [{ value: ERASE, label: optionLabel(BsFillEraserFill, 'Erase', compact) }]
			: []),
	];

	const handleShapeChange = value => {
		if (value === ERASE) {
			startErase();
			return;
		}
		dispatch({ type: 'setAnnotationDrawTool', payload: value });
		if (!isDrawing) goDraw();
	};

	const modeOptions = [
		{ value: DRAW, label: optionLabel(FiEdit3, 'Draw', compact) },
		{ value: SELECT, label: optionLabel(FiMousePointer, 'Select', compact) },
	];

	return (
		<Flex align="center" gap="small">
			{hasModes && (
				<Tooltip title="Draw (D) new boxes, or Select (V) a box to move, resize, relabel or remove it.">
					<Segmented
						options={modeOptions}
						value={mode}
						onChange={onModeChange}
						disabled={disabled}
						aria-label="Mode"
					/>
				</Tooltip>
			)}
			{(!hasModes || mode === DRAW) && (
				<Tooltip
					title={
						showErase
							? 'Pick a shape to draw, or Erase (E) boxes in the open tile. D returns to drawing.'
							: 'Pick a shape to draw.'
					}>
					<Segmented
						options={shapeOptions}
						value={isDrawing ? (annotationDrawTool ?? tools[0]) : ERASE}
						onChange={handleShapeChange}
						disabled={disabled}
						aria-label="Draw tool"
					/>
				</Tooltip>
			)}
			<Tooltip title="Rotate boxes while modifying them (R)">
				<Button
					type={isRotateEnabled ? 'primary' : 'default'}
					icon={<TbRotate360 />}
					onClick={toggleRotation}
					disabled={disabled}
					aria-label="Rotate"
					aria-pressed={isRotateEnabled}
					aria-keyshortcuts="R"
				/>
			</Tooltip>
		</Flex>
	);
};

export default DrawToolPicker;
